import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { PACKAGES } from "../data/site";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import Logo from "../components/Logo";
import GoogleButton from "../components/GoogleButton";
import { User, Users } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isAgentInvite = params.get("role") === "agent";
  const [role, setRole] = useState(isAgentInvite ? "agent" : "customer");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", agent_referral: "", salary: "", surname: "", date_of_birth: "", agent_code: "" });
  const [pkg, setPkg] = useState(params.get("package") || "basic");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [agentError, setAgentError] = useState("");

  useEffect(() => {
    const p = PACKAGES.find((x) => x.id === pkg);
    if (p && !amount) setAmount(String(p.minValue));
    // eslint-disable-next-line
  }, [pkg]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAgentError("");
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        role,
        phone: form.phone || null,
        agent_referral: role === "customer" ? form.agent_referral || null : null,
        salary: pkg === "pro" && form.salary ? Number(form.salary) : null,
        surname: role === "agent" ? form.surname : null,
        date_of_birth: role === "agent" ? form.date_of_birth : null,
        agent_code: role === "agent" ? form.agent_code : null,
      });

      if (role === "customer") {
        try {
          await api.post("/me/package", {
            package_id: pkg,
            monthly_amount: Number(amount) || PACKAGES.find((x) => x.id === pkg).minValue,
            salary: pkg === "pro" && form.salary ? Number(form.salary) : null,
          });
        } catch (pkgErr) { toast.error(apiError(pkgErr)); }
      }

      toast.success("Welcome to Givers Driven Funds!");
      navigate("/dashboard");
    } catch (err) {
      const msg = apiError(err);
      toast.error(msg);
      if (role === "agent") setAgentError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6"><Link to="/"><Logo className="h-12" /></Link></div>
        <div className="bg-white rounded-2xl border border-slate-200 gdf-card-shadow p-6 sm:p-8">
          <h2 className="text-2xl font-head font-bold text-[#0A192F] text-center">Create your account</h2>
          <p className="text-slate-500 text-sm text-center mt-1">Join the community where you always get a YES</p>

          {/* Role toggle — agent option only shown to invited applicants via ?role=agent */}
          {isAgentInvite && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setRole("customer")} data-testid="role-customer" className={`rounded-xl border-2 p-4 text-left transition-all ${role === "customer" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                <User className={`w-5 h-5 ${role === "customer" ? "text-emerald-600" : "text-slate-400"}`} />
                <p className="font-head font-semibold text-[#0A192F] mt-2">Member</p>
                <p className="text-xs text-slate-500">Join & get help</p>
              </button>
              <button type="button" onClick={() => setRole("agent")} data-testid="role-agent" className={`rounded-xl border-2 p-4 text-left transition-all ${role === "agent" ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                <Users className={`w-5 h-5 ${role === "agent" ? "text-emerald-600" : "text-slate-400"}`} />
                <p className="font-head font-semibold text-[#0A192F] mt-2">Agent</p>
                <p className="text-xs text-slate-500">By invitation only</p>
              </button>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label>{role === "agent" ? "First name(s)" : "Full name"}</Label><Input required value={form.name} onChange={set("name")} data-testid="register-name" className="mt-1" placeholder={role === "agent" ? "e.g. Messy Junior" : "e.g. John Smith"} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" className="mt-1" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-1" placeholder="0..." /></div>
            </div>
            <div><Label>Password</Label><Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password" className="mt-1" /></div>

            {role === "agent" && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Surname</Label><Input required value={form.surname} onChange={set("surname")} data-testid="register-surname" className="mt-1" placeholder="e.g. Peterson" /></div>
                  <div><Label>Date of birth</Label><Input type="date" required value={form.date_of_birth} onChange={set("date_of_birth")} data-testid="register-dob" className="mt-1" /></div>
                </div>
                <div>
                  <Label>Agent authorization code</Label>
                  <Input required value={form.agent_code} onChange={set("agent_code")} data-testid="register-auth-code" className="mt-1" placeholder="Enter the code you were given" />
                  <p className="text-xs text-slate-400 mt-1">Agent registration is by invitation only. If you don't have a valid authorization code, please register as a member.</p>
                </div>
                {agentError && (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-testid="agent-auth-error">
                    {agentError}
                  </div>
                )}
              </>
            )}

            {role === "customer" && (
              <>
                <div>
                  <Label>Choose your package</Label>
                  <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {PACKAGES.map((p) => (
                      <button type="button" key={p.id} onClick={() => { setPkg(p.id); setAmount(String(p.minValue)); }} data-testid={`register-pkg-${p.id}`} className={`rounded-lg border-2 p-2 text-center transition-all ${pkg === p.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                        <p className="text-xs font-semibold text-[#0A192F]">{p.name.split(" ")[0]}</p>
                        <p className="text-[10px] text-slate-500">{p.min}</p>
                      </button>
                    ))}
                  </div>
                </div>
                {pkg === "pro" && (
                  <div><Label>Your monthly salary (R)</Label><Input type="number" value={form.salary} onChange={set("salary")} data-testid="register-salary" className="mt-1" placeholder="Pro minimum is 10% of this" /></div>
                )}
                <div>
                  <Label>Monthly amount you'll pay (R)</Label>
                  <Input type="number" min={10} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="register-amount" className="mt-1" />
                  <p className="text-xs text-slate-400 mt-1">Minimum for {PACKAGES.find(p=>p.id===pkg)?.name} is {PACKAGES.find(p=>p.id===pkg)?.min}. Pay more and we auto-upgrade you.</p>
                </div>
                <div><Label>Agent referral code (optional)</Label><Input value={form.agent_referral} onChange={set("agent_referral")} data-testid="register-agent-code" className="mt-1" placeholder="If an agent referred you" /></div>
              </>
            )}

            <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800" data-testid="register-docs-note">
              No documents needed right now. After you register, you can upload your certified ID, profile picture and bank statements anytime from the <span className="font-semibold">Documents</span> tab in your dashboard.
            </div>

            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-11">{loading ? "Creating account..." : "Create account"}</Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200" /> OR <div className="flex-1 h-px bg-slate-200" /></div>
          <GoogleButton label="Sign up with Google" />
          <p className="text-[11px] text-slate-400 text-center mt-2">Google sign-up creates a member account. You can add ID & picture in your dashboard.</p>

          <p className="mt-5 text-sm text-slate-500 text-center">Already have an account? <Link to="/login" className="text-emerald-600 font-semibold" data-testid="register-to-login">Log in</Link></p>
        </div>
      </div>
    </div>
  );
}
